import React, { useState, useEffect } from "react";
import hosys from "@/api/api";
import { useVenue } from "@/components/shared/VenueContext";
import PageHeader from "@/components/shared/PageHeader";
import QuickLoader from "@/components/shared/QuickLoader";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import HarassmentDocuments from "@/components/harassment/HarassmentDocuments";
import HarassmentTrainingList from "@/components/harassment/HarassmentTrainingList";
import HarassmentCasesList from "@/components/harassment/HarassmentCasesList";
import {
  FileText, GraduationCap, ShieldAlert, Lock,
  CheckCircle2, Clock, AlertTriangle, Users,
} from "lucide-react";
import moment from "moment";
import "moment/locale/ca";
moment.locale("ca");

export default function HarassmentProtocol() {
  const { currentVenue, userRole, loading: venueLoading } = useVenue();
  const [trainings, setTrainings] = useState([]);
  const [cases, setCases] = useState([]);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");

  const isAdmin = userRole === "superadmin" || userRole === "admin";

  useEffect(() => {
    if (currentVenue) loadAll();
  }, [currentVenue]);

  const loadAll = async () => {
    setLoading(true);
    const [tr, cs, dc] = await Promise.all([
      hosys.entities.HarassmentTraining.filter({ venue_id: currentVenue.id }, "-training_date"),
      hosys.entities.HarassmentCase.filter({ venue_id: currentVenue.id }, "-report_date"),
      hosys.entities.Document.filter({ venue_id: currentVenue.id, category: "protocol" }, "-created_date"),
    ]);
    setTrainings(tr);
    setCases(cs);
    setDocs(dc);
    setLoading(false);
  };

  if (venueLoading || loading) return <QuickLoader />;

  // Stats
  const openCases = cases.filter(c => c.status === "received" || c.status === "investigating");
  const trainedWorkers = new Set(trainings.map(t => t.worker_name)).size;
  const expiredTrainings = trainings.filter(t => t.expiry_date && moment(t.expiry_date).isBefore(moment(), "day"));
  const docsCount = docs.filter(d =>
    d.description?.includes("protocol") || d.description?.includes("equality") || d.description?.includes("complaint")
    || d.name?.toLowerCase().includes("protocol") || d.name?.toLowerCase().includes("igualtat") || d.name?.toLowerCase().includes("denúncia")
  ).length;

  const alerts = [];
  if (docsCount < 3) alerts.push({ level: "warning", msg: `Falten documents obligatoris (${3 - docsCount} pendent${3 - docsCount > 1 ? "s" : ""}).` });
  if (expiredTrainings.length > 0) alerts.push({ level: "warning", msg: `${expiredTrainings.length} formació(ns) caducada(es). Cal renovar-les.` });
  if (openCases.length > 0) alerts.push({ level: "danger", msg: `${openCases.length} cas(os) obert(s) pendent(s) de resolució.` });

  return (
    <div className="space-y-5">
      <PageHeader
        title="Protocol Assetjament Laboral"
        subtitle={`${currentVenue?.name} · Compliment Llei 15/2022 i Conveni Col·lectiu`}
      />

      {/* Alerts */}
      {alerts.length > 0 && (
        <div className="space-y-2">
          {alerts.map((a, i) => (
            <div key={`alert-${i}`} className={`rounded-2xl p-3 flex items-center gap-3 ${
              a.level === "danger" ? "bg-red-50 border border-red-200 text-red-800" : "bg-amber-50 border border-amber-200 text-amber-800"
            }`}>
              <AlertTriangle className="w-4 h-4 flex-shrink-0" />
              <p className="text-sm font-medium">{a.msg}</p>
            </div>
          ))}
        </div>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <KpiCard icon={FileText} label="Documents" value={`${Math.min(docsCount, 3)}/3`}
          color={docsCount >= 3 ? "text-emerald-600" : "text-amber-600"}
          bg={docsCount >= 3 ? "bg-emerald-50" : "bg-amber-50"}
          iconColor={docsCount >= 3 ? "text-emerald-500" : "text-amber-500"}
          onClick={() => setActiveTab("documents")} />
        <KpiCard icon={GraduationCap} label="Treballadors formats" value={trainedWorkers}
          color="text-purple-600" bg="bg-purple-50" iconColor="text-purple-500"
          onClick={() => setActiveTab("training")} />
        <KpiCard icon={ShieldAlert} label="Casos oberts" value={openCases.length}
          color={openCases.length > 0 ? "text-red-600" : "text-emerald-600"}
          bg={openCases.length > 0 ? "bg-red-50" : "bg-emerald-50"}
          iconColor={openCases.length > 0 ? "text-red-500" : "text-emerald-500"}
          onClick={() => setActiveTab("cases")} />
        <KpiCard icon={Users} label="Total formacions" value={trainings.length}
          color="text-blue-600" bg="bg-blue-50" iconColor="text-blue-500"
          onClick={() => setActiveTab("training")} />
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-4 h-auto gap-1 p-1 bg-gray-100 rounded-xl">
          <TabsTrigger value="overview" className="rounded-lg text-xs py-2 data-[state=active]:bg-white data-[state=active]:shadow-sm">
            Resum
          </TabsTrigger>
          <TabsTrigger value="documents" className="rounded-lg text-xs py-2 data-[state=active]:bg-white data-[state=active]:shadow-sm">
            Documents
            {docsCount < 3 && <span className="ml-1 text-[10px] bg-amber-100 text-amber-600 rounded-full px-1.5">{3 - docsCount}</span>}
          </TabsTrigger>
          <TabsTrigger value="training" className="rounded-lg text-xs py-2 data-[state=active]:bg-white data-[state=active]:shadow-sm">
            Formació
            {trainings.length > 0 && <span className="ml-1 text-[10px] bg-purple-100 text-purple-600 rounded-full px-1.5">{trainings.length}</span>}
          </TabsTrigger>
          <TabsTrigger value="cases" className="rounded-lg text-xs py-2 data-[state=active]:bg-white data-[state=active]:shadow-sm">
            <Lock className="w-3 h-3 mr-1" />
            Canal intern
            {openCases.length > 0 && <span className="ml-1 text-[10px] bg-red-100 text-red-600 rounded-full px-1.5">{openCases.length}</span>}
          </TabsTrigger>
        </TabsList>

        {/* OVERVIEW */}
        <TabsContent value="overview" className="space-y-4 mt-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <OverviewCard icon={FileText} iconBg="bg-blue-50" iconColor="text-blue-600"
              title="Documentació obligatòria" subtitle={`${Math.min(docsCount, 3)}/3 documents`}
              status={docsCount >= 3 ? "ok" : "warning"}
              onClick={() => setActiveTab("documents")}>
              <p className="text-xs text-gray-500">Protocol d'assetjament, Pla d'igualtat i Procediment de denúncia</p>
            </OverviewCard>

            <OverviewCard icon={GraduationCap} iconBg="bg-purple-50" iconColor="text-purple-600"
              title="Registre de formació" subtitle={`${trainedWorkers} treballadors · ${trainings.length} sessions`}
              status={trainings.length === 0 ? "empty" : expiredTrainings.length > 0 ? "warning" : "ok"}
              onClick={() => setActiveTab("training")}>
              <p className="text-xs text-gray-500">Formació periòdica obligatòria en prevenció d'assetjament</p>
              {expiredTrainings.length > 0 && (
                <p className="text-xs text-amber-600 font-medium">{expiredTrainings.length} formació(ns) caducada(es)</p>
              )}
            </OverviewCard>

            <OverviewCard icon={ShieldAlert} iconBg="bg-red-50" iconColor="text-red-600"
              title="Canal intern de denúncia" subtitle={`${openCases.length} obert(s) · ${cases.filter(c => c.status === "resolved").length} resolt(s)`}
              status={openCases.length > 0 ? "warning" : cases.length === 0 ? "empty" : "ok"}
              onClick={() => setActiveTab("cases")}>
              <div className="flex items-center gap-1.5 text-[11px] text-amber-700">
                <Lock className="w-3 h-3" /> Registre confidencial d'accés restringit
              </div>
            </OverviewCard>

            <Card className="p-4 border-0 shadow-sm bg-gradient-to-br from-[#7B2D3F]/5 to-white">
              <h4 className="font-semibold text-gray-900 text-sm mb-2">Marc legal aplicable</h4>
              <div className="space-y-1.5 text-xs text-gray-500">
                <div className="flex items-start gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 mt-0.5 flex-shrink-0" /><span>Llei 15/2022 d'igualtat de tracte i no discriminació</span></div>
                <div className="flex items-start gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 mt-0.5 flex-shrink-0" /><span>Llei Orgànica 3/2007 per a la igualtat efectiva de dones i homes</span></div>
                <div className="flex items-start gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 mt-0.5 flex-shrink-0" /><span>Estatut dels Treballadors (art. 4.2.e i 54.2.g)</span></div>
                <div className="flex items-start gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 mt-0.5 flex-shrink-0" /><span>LRJS - Procediment especial assetjament</span></div>
                <div className="flex items-start gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 mt-0.5 flex-shrink-0" /><span>Convenio Colectivo del sector d'hostaleria</span></div>
              </div>
            </Card>
          </div>
        </TabsContent>

        {/* DOCUMENTS */}
        <TabsContent value="documents" className="mt-4">
          <div className="mb-4">
            <h3 className="font-semibold text-gray-900">Documentació Obligatòria</h3>
            <p className="text-xs text-gray-400 mt-0.5">Puja i gestiona els documents requerits per a la prevenció de l'assetjament laboral</p>
          </div>
          <HarassmentDocuments venueId={currentVenue.id} docs={docs} onDocsChanged={loadAll} isAdmin={isAdmin} />
        </TabsContent>

        {/* TRAINING */}
        <TabsContent value="training" className="mt-4">
          <div className="mb-4">
            <h3 className="font-semibold text-gray-900">Registre de Formació</h3>
            <p className="text-xs text-gray-400 mt-0.5">{trainings.length} registre(s) · {trainedWorkers} treballador(s) format(s)</p>
          </div>
          <HarassmentTrainingList
            items={trainings}
            venueId={currentVenue.id}
            onChanged={loadAll}
            isAdmin={isAdmin}
          />
        </TabsContent>

        {/* CASES / CANAL INTERN */}
        <TabsContent value="cases" className="mt-4">
          <div className="mb-4">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
              <Lock className="w-4 h-4 text-amber-600" />
              Canal Intern de Comunicació
            </h3>
            <p className="text-xs text-gray-400 mt-0.5">Registre confidencial d'incidències, conflictes laborals i situacions de risc</p>
          </div>
          <HarassmentCasesList
            items={cases}
            venueId={currentVenue.id}
            onChanged={loadAll}
            isAdmin={isAdmin}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, color, bg, iconColor, onClick }) {
  return (
    <Card className={`p-4 border-0 shadow-sm cursor-pointer hover:shadow-md transition-all ${bg}`} onClick={onClick}>
      <div className="flex items-center gap-3">
        <Icon className={`w-6 h-6 ${iconColor}`} />
        <div>
          <p className={`text-2xl font-bold ${color}`}>{value}</p>
          <p className="text-xs text-gray-500 leading-tight">{label}</p>
        </div>
      </div>
    </Card>
  );
}

function OverviewCard({ icon: Icon, iconBg, iconColor, title, subtitle, status, onClick, children }) {
  const border = status === "ok" ? "border-l-emerald-400" : status === "warning" ? "border-l-amber-400" : "border-l-gray-200";
  return (
    <Card className={`p-4 border-0 shadow-sm border-l-4 ${border} cursor-pointer hover:shadow-md transition-all`} onClick={onClick}>
      <div className="flex items-center gap-2.5 mb-3">
        <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${iconBg}`}>
          <Icon className={`w-4 h-4 ${iconColor}`} />
        </div>
        <div>
          <h4 className="font-semibold text-gray-900 text-sm">{title}</h4>
          <p className="text-[11px] text-gray-400">{subtitle}</p>
        </div>
      </div>
      <div className="space-y-1">{children}</div>
    </Card>
  );
}